import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { blank, format_datetime, generateId, OptionalValue, spf, to_local_datetime, to_utc_datetime, transform, when } from '../../../../utils/common';
import { BehaviorSubject } from 'rxjs';
import { PickerType } from 'ng-pick-datetime/date-time/date-time.class';
import moment, { Moment } from 'moment';

@Component({
  selector: 'fieldmagic-calendar-input',
  template: `
    <div
      *ngIf="! standalone"
      class="form-group"
    >
      <div class="d-flex d-flex-gap">
        <fieldmagic-text
          purpose="input"
          [content]="label"
          [withRequiredMarker]="withRequiredMarker"
        >
        </fieldmagic-text>
      </div>
      <ng-container *ngTemplateOutlet="input"></ng-container>
    </div>

    <ng-container
      *ngIf="standalone"
      [ngTemplateOutlet]="input"
    >
    </ng-container>

    <ng-template #input>
      <div class="input-group">
        <input
          [(ngModel)]="value"
          [value]="value"
          type="text"
          [attr.id]="id"
          class="form-control fieldmagic-datetime-input"
          [owlDateTime]="calendar"
          [placeholder]="placeholder"
          (ngModelChange)="onChanged($event)"
          readonly
          [disabled]="disabled$ | async"
          [matTooltip]="tooltip$ | async | translate"
        />

        <fieldmagic-icon
          *ngIf="clearable && (value | filled)"
          icon="times"
          purpose="clicking"
          class="fieldmagic-datetime-clear-button"
          (click)="onClear()"
        >
        </fieldmagic-icon>

        <button
          [matTooltip]="(btnTooltip$ | async) | translate"
          class="fieldmagic-datetime-button"
          [owlDateTimeTrigger]="calendar"
          [disabled]="disabled$ | async"
        >
          <div class="d-flex justify-content-center">
            <fieldmagic-icon
              icon="calendar"
            >
            </fieldmagic-icon>
          </div>
        </button>

        <owl-date-time
          #calendar
          [pickerType]="type$ | async"
        >
        </owl-date-time>
      </div>

      <fieldmagic-input-errors
        *ngIf="errors | filled"
        [errors]="errors"
      ></fieldmagic-input-errors>
    </ng-template>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CalendarInputComponent),
      multi: true,
    },
  ],
  styles: [
    `.fieldmagic-datetime-input {
      height: 40px;
      font-size: 12px;
    }`,
    `.fieldmagic-datetime-button {
      width: 38px;
      height: 40px;
      background: #0e76bc;
      color: #ffffff;
      border-top-right-radius: 5px;
      border-bottom-right-radius: 5px;
      border: #0e76bc;
      outline: none;
      cursor: pointer;
    }`,
    `.fieldmagic-datetime-clear-button {
      position: absolute;
      right: 50px;
      top: 10px;
      color: #495057;
      font-size: 14px;
      z-index: 999;
    }`,
  ]
})
export class CalendarInputComponent implements OnInit, ControlValueAccessor {
  @Input() id: string = spf('datetime:%s', {
    args: [generateId()],
  });

  @Input() label?: OptionalValue<Moment>;

  @Input() errors: string | string[] = [];

  @Input() standalone: boolean = true;

  @Input() withRequiredMarker: boolean = false;

  @Input() placeholder?: string = '';

  @Input() clearable: boolean = true;

  @Input()
  set disabled(value: boolean) {
    this.disabled$.next(value);
  }

  @Input()
  set type(value: 'date' | 'datetime') {
    this.type$.next(when<PickerType>(value == 'date', {
      then: () => 'calendar',
      else: () => 'both',
    }));
  }

  @Input()
  set tooltip(value: OptionalValue<string>) {
    this.tooltip$.next(value);
  }

  @Output('change') $onChanged = new EventEmitter<CalendarInputChangeEvent>();

  value?: OptionalValue<Moment>

  _onChanged?: (value: OptionalValue<string>) => void;
  _onTouched?: () => void;

  readonly type$ = new BehaviorSubject<PickerType>('calendar');
  readonly tooltip$ = new BehaviorSubject<OptionalValue<string>>(null);
  readonly btnTooltip$ = new BehaviorSubject<string>('Please click here to select a date.');
  readonly disabled$ = new BehaviorSubject<boolean>(false);

  ngOnInit(): void {
    if (this.type$.value == 'both') {
      this.btnTooltip$.next('Please click here to select a date and time.');
    }
  }

  registerOnChange = (fn: any) => this._onChanged = fn;
  registerOnTouched = (fn: any) => this._onTouched = fn;

  writeValue = (obj: string) => this.value = this._formatValue(obj);
  setDisabledState = (disabled: boolean) => this.disabled$.next(disabled);

  onChanged(event: OptionalValue<Moment>): void {
    const value = transform(event, {
      transformer: (value) => {
        const type = this.type$.value;

        if (type == 'calendar') {
          return format_datetime(value.toDate(), {
            format: 'YYYY-MM-DD',
          });
        }

        return format_datetime(to_utc_datetime(value.toDate()), {
          format: 'YYYY-MM-DDTHH:mm:ss',
        });
      },
    });

    if (this._onChanged) {
      this._onChanged(value);
    }

    this.$onChanged.emit(value);
  }

  onClear(): void {
    this.value = null;

    if (this._onChanged) {
      this._onChanged.call(null);
    }

    this.$onChanged.emit(null);
  }

  private _formatValue(value: OptionalValue<string>): OptionalValue<Moment> {
    if (blank(value)) {
      return null;
    }

    return moment(format_datetime(to_local_datetime(value), {
      format: when(this.type$.value == 'calendar', {
        then: () => 'll',
        else: () => 'lll',
      }),
    }));
  }
}

export type CalendarInputChangeEvent = OptionalValue<string>;